import React, { useEffect, useState, useMemo } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  ModalHeader,
  ModalHeaderTitle,
} from '@antoree/ant-ui';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import styled from 'styled-components';

// Imports from other components/services
import { getActiveBanners, IBanner } from 'services';
import { useGetSessionsMissingEvaluation } from 'services/session/getSessionsMissingEvaluation';
import {
  WarningNotificationContent,
  WarningNotificationHeader,
} from '../WarningNotification';
import Holiday from 'assets/images/30415holiday.jpeg.jpeg';

const NavButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0 10px;
  &:hover {
    color: #333;
  }
  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const Dot = styled.div<{ active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => (props.active ? '#0aa263' : '#ddd')};
  margin: 0 4px;
  cursor: pointer;
  transition: background-color 0.3s;
`;

const ActionButton = styled.a<{ buttonColor?: string; textColor?: string }>`
  display: inline-block;
  background-color: ${props => props.buttonColor || '#0aa263'};
  color: ${props => props.textColor || '#fff'};
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

type NotificationType = 'salary' | 'holiday' | 'warning' | 'banner';

interface SlideItem {
  id: string;
  type: NotificationType;
  header?: React.ReactNode;
  content: React.ReactNode;
  data?: any; // Original data for reference
}

const SlideContainer = styled.div`
  height: 500px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const modalStyle = { width: '900px', maxWidth: '900px' };

const UnifiedNotification = () => {
  const intl = useIntl();
  const [slides, setSlides] = useState<SlideItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // --- 1. Warning Data Fetching ---
  const {
    data: warningData,
    isLoading: isWarningLoading,
  } = useGetSessionsMissingEvaluation({
    pageSize: 50,
    page: 1,
  });

  const warningCourses = warningData?.data?.courses;

  useEffect(() => {
    // Only run when warning loading finishes
    if (isWarningLoading) {
      return;
    }

    const initializeSlides = async () => {
      const newSlides: SlideItem[] = [];

      // --- 2. Salary / Holiday Logic ---
      const isHoliday = moment().isBetween(
        '2025-04-22',
        '2025-05-04',
        undefined,
        '[]',
      );
      // For now, assume visible if logic matches MainLayout (MainLayout uses a toggle 'visible' state, here we'll just check dates)
      // Actually MainLayout logic is: isVisiable && (Holiday OR Salary). We'll assume true for now or replicate availability check if needed.
      // Copying the hardcoded date logic from MainLayout:

      if (isHoliday) {
        newSlides.push({
          id: 'holiday_notification',
          type: 'holiday',
          header: (
            <ModalHeader>
              <ModalHeaderTitle>
                <Text className="text-2xl">
                  <FormattedMessage
                    id="notification.holiday_title"
                    defaultMessage="Holiday Notification"
                  />
                </Text>
              </ModalHeaderTitle>
            </ModalHeader>
          ),
          content: (
            <div className="text-center w-full h-full flex flex-col justify-center items-center">
              <div style={{ marginBottom: 16, fontSize: '16px' }}>
                Please remember to update your available schedule for trial
                course!
              </div>
              <img
                src={Holiday}
                alt="Holiday"
                style={{
                  maxWidth: '100%',
                  maxHeight: '420px',
                  objectFit: 'contain',
                }}
              />
            </div>
          ),
        });
      } else {
        // Salary Notification
        newSlides.push({
          id: 'salary_notification',
          type: 'salary',
          header: (
            <ModalHeader>
              <ModalHeaderTitle>
                <Text className="text-2xl">
                  <FormattedMessage
                    id="notification.salary_title"
                    defaultMessage="Salary Period"
                  />
                </Text>
              </ModalHeaderTitle>
            </ModalHeader>
          ),
          content: (
            <div
              style={{
                maxWidth: '600px',
                margin: '0 auto',
                textAlign: 'left',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <div
                className="mb-4 text-success"
                style={{
                  color: '#0aa263',
                  fontWeight: 'bold',
                  fontSize: '1.2em',
                  textAlign: 'center',
                }}
              >
                <FormattedMessage
                  id="notification.salary_period"
                  defaultMessage="From {date1} to {date2}:"
                  values={{
                    date1: intl.formatDate(moment().date(16).toDate(), {
                      month: 'long',
                      day: 'numeric',
                    }),
                    date2: intl.formatDate(moment().date(23).toDate(), {
                      month: 'long',
                      day: 'numeric',
                    }),
                  }}
                />
              </div>
              <p style={{ marginBottom: 12, lineHeight: 1.6 }}>
                <FormattedMessage
                  id="notification.salary_withheld_warning"
                  defaultMessage="- If a Teacher quits teaching during this time, their salary will be withheld, and their salary will be transferred to {date}"
                  values={{
                    date: (
                      <span
                        className="text-success"
                        style={{ color: '#0aa263', fontWeight: 'bold' }}
                      >
                        {' '}
                        {intl.formatDate(
                          moment().add(1, 'months').date(5).toDate(),
                          { month: 'long', day: 'numeric' },
                        )}
                      </span>
                    ),
                  }}
                />
              </p>
              <p style={{ lineHeight: 1.6 }}>
                <FormattedMessage
                  id="notification.student_absent_instruction"
                  defaultMessage="- If the Student is absent, please contact the Course's ACS to report the Student's request for absence so that you can receive the first salary transfer on {date}."
                  values={{
                    date: (
                      <span
                        className="text-success"
                        style={{ color: '#0aa263', fontWeight: 'bold' }}
                      >
                        {' '}
                        {intl.formatDate(moment().date(26).toDate(), {
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    ),
                  }}
                />
              </p>
            </div>
          ),
        });
      }

      // --- 3. Warning Logic ---
      // We rely on the hook result which updates over time.
      // This useEffect dependency array will include warningCourses.
      if (warningCourses && warningCourses.length > 0) {
        newSlides.push({
          id: 'warning_notification',
          type: 'warning',
          header: <WarningNotificationHeader courses={warningCourses} />,
          content: <WarningNotificationContent courses={warningCourses} />,
        });
      } else if (warningCourses && warningCourses.length === 0) {
        // Show success message when there are no warnings
        newSlides.push({
          id: 'warning_notification',
          type: 'warning',
          header: (
            <ModalHeader>
              <ModalHeaderTitle>
                <Text className="text-2xl">
                  <FormattedMessage
                    id="warning_notification.title"
                    defaultMessage="Notification"
                  />
                </Text>
              </ModalHeaderTitle>
            </ModalHeader>
          ),
          content: (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Text
                className="text-success"
                style={{ fontSize: '18px', fontWeight: 600 }}
              >
                <FormattedMessage
                  id="warning_notification.no_warnings"
                  defaultMessage="Great! You have no courses that need warnings."
                />
              </Text>
            </div>
          ),
        });
      }

      // --- 4. Banner Logic ---
      try {
        const bannersData = await getActiveBanners('all');
        const now = moment();
        const isDev = process.env.NODE_ENV === 'development';

        const popupBanners = bannersData.filter((banner: IBanner) => {
          let isValid = true;
          const start = moment(banner.startAt);
          const end = moment(banner.endAt);
          const isActive =
            (banner as any).isActive !== undefined
              ? (banner as any).isActive
              : 1;

          // Date check
          if (!now.isBetween(start, end, undefined, '[]') && !isDev)
            isValid = false;

          // Active check - if the API returns inactive banners for some reason
          if (isActive === 0 && !isDev) isValid = false;

          // Audience check
          if (
            isValid &&
            banner.targetAudience &&
            banner.targetAudience !== 'all' &&
            banner.targetAudience !== 'teachers'
          ) {
            isValid = false;
          }

          // Mode check (Popup only)
          const mode = banner.metadata?.displayMode;
          const isPopup = mode === 'popup' || (!mode && banner.imageUrl); // Fallback logic
          if (!isPopup) isValid = false;

          return isValid;
        });

        // Add Banners to slides
        popupBanners.forEach((banner: IBanner) => {
          const getLocalizedText = (i18nData: any, legacyText: string) => {
            if (i18nData && i18nData['vi']) return i18nData['vi']; // Default to VI for simplicity or use context
            return legacyText;
          };

          const title = getLocalizedText(banner.titleI18n, banner.titleLegacy);

          newSlides.push({
            id: `banner_${banner.id}`,
            type: 'banner',
            header: title ? (
              <ModalHeader>
                <ModalHeaderTitle>
                  <Text className="text-2xl">{title}</Text>
                </ModalHeaderTitle>
              </ModalHeader>
            ) : (
              <ModalHeader>
                {/* Empty spacer for consistency (height of header ~24px usually) */}
                <div style={{ height: '24px' }}></div>
              </ModalHeader>
            ),
            content: (
              <div
                style={{
                  position: 'relative',
                  textAlign: 'center',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <img
                  src={banner.imageUrl}
                  alt={banner.titleLegacy}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '440px', // slightly less than container to leave room for button
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
                {banner.actionType === 'link' && banner.actionUrl && (
                  <div style={{ marginTop: 16 }}>
                    <ActionButton
                      href={banner.actionUrl}
                      target="_blank"
                      buttonColor={banner.textColor} // Note: Mapping might be reversed in DB, using as provided
                      textColor={banner.backgroundColor}
                    >
                      {getLocalizedText(
                        banner.actionLabelI18n,
                        banner.actionLabelLegacy || '',
                      ) || (
                        <FormattedMessage
                          id="common.view_details"
                          defaultMessage="View Details"
                        />
                      )}
                    </ActionButton>
                  </div>
                )}
              </div>
            ),
          });
        });
      } catch (error) {
        console.error(
          'Failed to fetch banners for unified notification',
          error,
        );
      }

      setSlides(newSlides);
      if (newSlides.length > 0) {
        setIsOpen(true);
      }
      setHasInitialized(true);
    };

    initializeSlides();
  }, [isWarningLoading, intl, warningCourses]);

  const [isAutoSliding, setIsAutoSliding] = useState(true);
  const [maxViewedIndex, setMaxViewedIndex] = useState(0);

  useEffect(() => {
    if (currentIndex > maxViewedIndex) {
      setMaxViewedIndex(currentIndex);
    }
  }, [currentIndex, maxViewedIndex]);

  // ... (data fetching) ...

  // Auto-slide effect
  useEffect(() => {
    if (!isOpen || slides.length <= 1 || !isAutoSliding) {
      return;
    }

    const timer = setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % slides.length);
    }, 3000);

    return () => clearTimeout(timer);
  }, [isOpen, slides, currentIndex, isAutoSliding]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleNext = () => {
    // User interaction stops auto-sliding
    setIsAutoSliding(false);
    if (currentIndex < slides.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    setIsAutoSliding(false);
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  if (!isOpen || slides.length === 0) return null;
  if (!slides[currentIndex]) return null;

  const currentSlide = slides[currentIndex];
  // Standardize modal width for consistency
  const modalStyle = { width: '900px', maxWidth: '900px' };

  // Check if user has viewed all slides at least once
  const hasViewedAll =
    slides.length === 0 || maxViewedIndex >= slides.length - 1;
  const isLastSlide = currentIndex === slides.length - 1;

  // Button should only be "Close" at the last slide
  const showCloseButton = hasViewedAll && isLastSlide;

  // Allow closing via X/Mask if all viewed
  const canDismiss = hasViewedAll;

  // Manual close via X button or mask click needs to be prevented if not allowed
  const onModalClose = () => {
    if (canDismiss) handleClose();
  };

  return (
    <Modal onClose={onModalClose} style={modalStyle}>
      <style>{`
          .euiModal__closeIcon {
             /* Show close icon if canDismiss is true, otherwise hide */
             display: ${canDismiss ? 'block' : 'none'} !important;
          }
        `}</style>

      {currentSlide.header}

      <ModalBody>
        <SlideContainer>
          <ContentWrapper>{currentSlide.content}</ContentWrapper>
        </SlideContainer>
      </ModalBody>

      <ModalFooter>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Previous Button */}
          <Button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            fill={false}
            color="text"
          >
            <Text>
              <FormattedMessage
                id="common.previous"
                defaultMessage="Previous"
              />
            </Text>
          </Button>

          {/* Dots Indicator */}
          <div style={{ display: 'flex' }}>
            {slides.length > 1 &&
              slides.map((_, idx) => (
                <Dot
                  key={idx}
                  active={idx === currentIndex}
                  onClick={() => {
                    setIsAutoSliding(false);
                    setCurrentIndex(idx);
                  }}
                />
              ))}
          </div>

          {/* Next / Close Button */}
          {showCloseButton ? (
            <Button onClick={handleClose} fill>
              <Text>
                <FormattedMessage id="common.close" defaultMessage="Close" />
              </Text>
            </Button>
          ) : (
            <Button onClick={handleNext} fill>
              <Text>
                <FormattedMessage id="common.next" defaultMessage="Next" />
              </Text>
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default UnifiedNotification;
